import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const FIELDS = [
  ["logo_url", "URL Logo Perusahaan"],
  ["nama_saham", "Nama Saham"],
  ["kode_saham", "Kode Saham"],
  ["sektor", "Sektor Saham"],
  ["range_harga", "Range Harga IPO"],
  ["tanggal_book_building", "Tanggal Book Building"],
  ["tanggal_penawaran_umum", "Tanggal Penawaran Umum"],
  ["tanggal_listing", "Tanggal Listing"],
  ["aset", "Aset Perusahaan"],
  ["pendapatan", "Pendapatan"],
  ["laba_rugi", "Laba / Rugi"],
  ["saham_ditawarkan", "Saham yang Ditawarkan"],
  ["nilai_saham", "Nilai Saham"],
  ["underwriter", "Penjamin Emisi (Underwriter)"],
  ["harga_final", "Harga Final IPO"],
];

const empty = Object.fromEntries(FIELDS.map(([k]) => [k, ""]));

const MATRIX_ROWS = FIELDS.filter(([k]) => k !== "logo_url");

const FORM_FIELDS = FIELDS.filter(([k]) => k !== "nilai_saham");

const angka = (teks) => Number(String(teks || "").replace(/\D/g, "")) || 0;

const hitungNilaiSaham = (form) => {
  const total = angka(form.saham_ditawarkan) * angka(form.harga_final) * 100;
  return total > 0 ? "Rp " + total.toLocaleString("id-ID") : "";
};

export default function IpoPage() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/ipo");
      setRows(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...empty, ...row });
    setOpen(true);
  };

  const save = async () => {
    if (!form.nama_saham || !form.kode_saham) return toast.error("Nama & Kode Saham wajib diisi");
    setSaving(true);
    try {
      const payload = Object.fromEntries(FIELDS.map(([k]) => [k, form[k] || ""]));
      if (editing) await api.put(`/ipo/${editing.id}`, payload);
      else await api.post("/ipo", payload);
      toast.success(editing ? "Data saham diperbarui" : "Saham baru ditambahkan");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Hapus data saham ${row.kode_saham}?`)) return;
    try {
      await api.delete(`/ipo/${row.id}`);
      toast.success("Data saham dihapus");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="ipo-page">
      <PageHeader
        eyebrow="Menu IPO"
        title="Daftar Saham IPO"
        description="Jadwal dan fundamental perusahaan yang akan melantai di Bursa Efek Indonesia."
      >
        {isAdmin && (
          <Button
            data-testid="ipo-add-btn"
            onClick={openNew}
            className="rounded-full bg-[#00d09c] font-semibold text-[#080b11] transition-colors duration-200 hover:bg-[#00e5ff]"
          >
            <Plus size={16} className="mr-1.5" /> Tambah Saham Baru
          </Button>
        )}
      </PageHeader>

      {loading ? (
        <div className="flex justify-center rounded-lg border border-[#1e2536] bg-[#10141d] py-16">
          <Loader2 className="animate-spin text-[#2b3550]" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-[#1e2536] bg-[#10141d]">
          <EmptyState
            icon={Building2}
            title="Belum ada data saham IPO"
            hint={isAdmin ? "Klik 'Tambah Saham Baru' untuk mulai mengisi data." : "Admin belum menambahkan data."}
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-[#1e2536] bg-[#10141d]">
          <div className="overflow-x-auto">
            <table data-testid="ipo-matrix" className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 min-w-[190px] border-b border-r border-[#1e2536] bg-[#0d1119] px-4 py-4 text-left text-xs uppercase tracking-[0.16em] text-[#4d586f]">
                    Keterangan
                  </th>
                  {rows.map((r) => (
                    <th
                      key={r.id}
                      data-testid={`ipo-col-${r.kode_saham}`}
                      className="min-w-[220px] border-b border-r border-[#1e2536] bg-[#0d1119] px-4 py-4 text-center align-middle last:border-r-0"
                    >
                      <div className="flex flex-col items-center gap-2">
                        {r.logo_url ? (
                          <img
                            src={r.logo_url}
                            alt={r.nama_saham}
                            className="h-11 max-w-[130px] object-contain"
                            onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                          />
                        ) : (
                          <div className="flex h-11 w-11 items-center justify-center rounded-md border border-[#1e2536] bg-[#10141d]">
                            <Building2 size={18} className="text-[#2b3550]" />
                          </div>
                        )}
                        {isAdmin && (
                          <div className="flex gap-1.5">
                            <Button
                              data-testid={`ipo-edit-btn-${r.kode_saham}`}
                              size="icon"
                              variant="outline"
                              onClick={() => openEdit(r)}
                              className="h-7 w-7 border-[#1e2536] bg-transparent text-[#8f9bb3] hover:text-[#00d09c]"
                            >
                              <Pencil size={13} />
                            </Button>
                            <Button
                              data-testid={`ipo-delete-btn-${r.kode_saham}`}
                              size="icon"
                              variant="outline"
                              onClick={() => remove(r)}
                              className="h-7 w-7 border-[#1e2536] bg-transparent text-[#8f9bb3] hover:text-[#ff3b30]"
                            >
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MATRIX_ROWS.map(([key, label]) => (
                  <tr key={key} className="transition-colors duration-150 hover:bg-white/[0.03]">
                    <th className="sticky left-0 z-10 border-b border-r border-[#1e2536] bg-[#0d1119] px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.14em] text-[#4d586f]">
                      {label}
                    </th>
                    {rows.map((r) => (
                      <td
                        key={r.id + key}
                        data-testid={`ipo-cell-${r.kode_saham}-${key}`}
                        className={`border-b border-r border-[#1e2536] px-4 py-3 text-center last:border-r-0 ${
                          key === "nama_saham"
                            ? "text-white"
                            : key === "kode_saham"
                            ? "font-mono-num font-semibold text-[#00d09c]"
                            : key === "harga_final"
                            ? "font-mono-num font-semibold text-[#00d09c]"
                            : key === "sektor"
                            ? "text-[#8f9bb3]"
                            : "font-mono-num text-white"
                        }`}
                      >
                        {r[key] || "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ipo-dialog" className="max-h-[88vh] overflow-y-auto border-[#1e2536] bg-[#10141d] sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editing ? `Edit Saham ${editing.kode_saham}` : "Tambah Saham Baru"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            {FORM_FIELDS.map(([key, label]) => (
              <div key={key} className={key === "logo_url" || key === "underwriter" ? "sm:col-span-2" : ""}>
                <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">{label}</Label>
                <Input
                  data-testid={`ipo-input-${key}`}
                  value={form[key] || ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="mt-1.5 border-[#1e2536] bg-[#0d1119] text-white focus-visible:ring-[#00d09c]"
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">
                Nilai Saham (otomatis)
              </Label>
              <Input
                data-testid="ipo-input-nilai_saham"
                readOnly
                value={hitungNilaiSaham(form) || "-"}
                className="font-mono-num mt-1.5 cursor-not-allowed border-[#1e2536] bg-[#0b0e15] text-[#00d09c]"
              />
              <p className="mt-1.5 text-xs text-[#4d586f]">
                Rumus: Saham yang Ditawarkan (lot) × Harga Final IPO × 100
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#1e2536] bg-transparent text-[#8f9bb3]">
              Batal
            </Button>
            <Button
              data-testid="ipo-save-btn"
              onClick={save}
              disabled={saving}
              className="rounded-full bg-[#00d09c] font-semibold text-[#080b11] hover:bg-[#00e5ff]"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
